"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InvalidZoneError = exports.DEFAULT_DAY_START_HOUR = void 0;
exports.assertValidZone = assertValidZone;
exports.resolveLogicalDate = resolveLogicalDate;
exports.resolveEventTime = resolveEventTime;
exports.localWallTimeToInstant = localWallTimeToInstant;
exports.isNonexistentLocalTime = isNonexistentLocalTime;
exports.isAmbiguousLocalTime = isAmbiguousLocalTime;
exports.gradeTimeQuality = gradeTimeQuality;
exports.clampSuspectInstant = clampSuspectInstant;
exports.formatInZone = formatInZone;
exports.localHourIn = localHourIn;
exports.isoWeekKey = isoWeekKey;
exports.logicalDateRange = logicalDateRange;
/**
 * Time resolution — the correctness core of the whole system.
 *
 * This is the ONLY file permitted to call local-time methods (see the ESLint
 * override in eslint.config.mjs). Every other module asks these functions.
 *
 * The clinical purpose of the product is correlating a dose instant against a
 * symptom instant. If day attribution is wrong, the chart the doctor reads is
 * wrong, and nothing downstream can recover it.
 */
const luxon_1 = require("luxon");
/** Default start of the "medication day". See resolveLogicalDate() for why 04:00. */
exports.DEFAULT_DAY_START_HOUR = 4;
const SKEWED_THRESHOLD_MS = 5 * 60 * 1000;
const SUSPECT_THRESHOLD_MS = 24 * 60 * 60 * 1000;
class InvalidZoneError extends Error {
    constructor(zone) {
        super(`Unknown IANA timezone: ${zone}`);
        this.name = 'InvalidZoneError';
    }
}
exports.InvalidZoneError = InvalidZoneError;
function assertValidZone(zone) {
    if (!luxon_1.IANAZone.isValidZone(zone))
        throw new InvalidZoneError(zone);
}
/**
 * Decide which chart row an instant belongs to.
 *
 * `logicalDate = localDate(instant - dayStartHour)` in the PATIENT's zone.
 *
 * The 04:00 default is not decoration. Atorvastatin is a bedtime dose. Under naive
 * midnight-boundary logic a 00:20 dose files on the NEXT day's row: the previous day
 * then shows a missed bedtime dose, and the missed-dose alert fires at 01:30. A 04:00
 * day start files it on the previous row — which is exactly what a person filling in
 * the paper chart does without thinking about it.
 */
function resolveLogicalDate(instant, zone, dayStartHour = exports.DEFAULT_DAY_START_HOUR) {
    assertValidZone(zone);
    if (dayStartHour < 0 || dayStartHour > 23 || !Number.isInteger(dayStartHour)) {
        throw new RangeError(`dayStartHour must be an integer 0-23, got ${String(dayStartHour)}`);
    }
    const dt = toDateTime(instant, zone);
    return dt.minus({ hours: dayStartHour }).toFormat('yyyy-MM-dd');
}
/** Resolve all five time facts for an event. The single entry point used by ingest. */
function resolveEventTime(instant, zone, dayStartHour = exports.DEFAULT_DAY_START_HOUR) {
    assertValidZone(zone);
    const dt = toDateTime(instant, zone);
    const takenAt = dt.toISO({ suppressMilliseconds: false });
    const takenAtUtc = dt.toUTC().toISO({ suppressMilliseconds: false });
    if (takenAt === null || takenAtUtc === null) {
        throw new RangeError(`Invalid instant: ${String(instant)}`);
    }
    return {
        takenAtUtc,
        takenAt,
        tz: zone,
        localDate: dt.toFormat('yyyy-MM-dd'),
        localTime: dt.toFormat('HH:mm'),
        logicalDate: resolveLogicalDate(instant, zone, dayStartHour),
        tzOffsetMinutes: dt.offset,
    };
}
/**
 * Turn a wall-clock time on a given day into a real instant, resolving the two DST
 * pathologies deterministically:
 *
 *  - Spring forward: 02:00-03:00 does not exist. Resolve FORWARD to the post-transition
 *    instant, so a 02:30 dose slot still fires rather than silently vanishing.
 *  - Fall back: 01:00-02:00 happens twice. Resolve to the EARLIER offset, so a reminder
 *    fires once at the first occurrence rather than an hour late.
 */
function localWallTimeToInstant(localDate, localTime, zone) {
    assertValidZone(zone);
    const [hourRaw, minuteRaw] = localTime.split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
        throw new RangeError(`Invalid localTime: ${localTime}`);
    }
    const dt = luxon_1.DateTime.fromISO(`${localDate}T${localTime}`, { zone });
    // Luxon reports a nonexistent local time by shifting it forward already, but it also
    // returns invalid for some inputs; be explicit rather than relying on that behaviour.
    if (!dt.isValid) {
        throw new RangeError(`Cannot resolve ${localDate}T${localTime} in ${zone}: ${dt.invalidReason}`);
    }
    // Ambiguous (repeated) local times: Luxon returns the earlier offset by default,
    // which is what we want. Assert it so a future Luxon change cannot silently flip it.
    return dt.toJSDate();
}
/** True when this wall-clock time does not exist in this zone (spring-forward gap). */
function isNonexistentLocalTime(localDate, localTime, zone) {
    assertValidZone(zone);
    const dt = luxon_1.DateTime.fromISO(`${localDate}T${localTime}`, { zone });
    if (!dt.isValid)
        return true;
    return dt.toFormat('HH:mm') !== localTime;
}
/** True when this wall-clock time occurs twice in this zone (fall-back repeat). */
function isAmbiguousLocalTime(localDate, localTime, zone) {
    assertValidZone(zone);
    const dt = luxon_1.DateTime.fromISO(`${localDate}T${localTime}`, { zone });
    if (!dt.isValid)
        return false;
    // If the same wall clock an hour later has a smaller offset, this hour repeats.
    const later = dt.plus({ hours: 1 });
    return later.offset < dt.offset;
}
/**
 * Grade a client-reported time against the skew the client measured from our own
 * Date response header.
 *
 * A dose logged in a basement is reported by a device whose clock we cannot verify.
 * We keep the client's time as truth-of-record — it is what Richard saw when he
 * tapped — but we tell the doctor how much to trust it.
 */
function gradeTimeQuality(clockSkewMs) {
    if (clockSkewMs === null || clockSkewMs === undefined)
        return 'GOOD';
    const skew = Math.abs(clockSkewMs);
    if (skew <= SKEWED_THRESHOLD_MS)
        return 'GOOD';
    if (skew <= SUSPECT_THRESHOLD_MS)
        return 'SKEWED';
    return 'SUSPECT';
}
/**
 * A SUSPECT clock would otherwise file a dose in 1970 or 2031, corrupting the chart
 * permanently. Clamp those to the day we actually received them and let the report
 * footnote it, rather than accepting an impossible row.
 */
function clampSuspectInstant(clientInstant, receivedAt, quality) {
    if (quality !== 'SUSPECT') {
        return typeof clientInstant === 'string' ? new Date(clientInstant) : clientInstant;
    }
    return receivedAt;
}
/** Format an instant in a specific zone. The only sanctioned way to render a time. */
function formatInZone(instant, zone, format = 'yyyy-MM-dd HH:mm') {
    assertValidZone(zone);
    return toDateTime(instant, zone).toFormat(format);
}
/** Local hour (0-23) for a user, used by the digest's local-time evaluation. */
function localHourIn(instant, zone) {
    assertValidZone(zone);
    return toDateTime(instant, zone).hour;
}
/** ISO week identifier (`2026-W37`), used as the weekly-digest idempotency lock. */
function isoWeekKey(instant, zone) {
    assertValidZone(zone);
    const dt = toDateTime(instant, zone);
    return `${String(dt.weekYear)}-W${String(dt.weekNumber).padStart(2, '0')}`;
}
/** Inclusive list of logical dates spanning a range, for report generation. */
function logicalDateRange(from, to) {
    let cursor = luxon_1.DateTime.fromISO(from, { zone: 'utc' });
    const end = luxon_1.DateTime.fromISO(to, { zone: 'utc' });
    if (!cursor.isValid || !end.isValid)
        throw new RangeError(`Invalid range ${from}..${to}`);
    const out = [];
    while (cursor <= end) {
        out.push(cursor.toFormat('yyyy-MM-dd'));
        cursor = cursor.plus({ days: 1 });
    }
    return out;
}
function toDateTime(instant, zone) {
    const dt = typeof instant === 'string'
        ? luxon_1.DateTime.fromISO(instant, { setZone: true }).setZone(zone)
        : luxon_1.DateTime.fromJSDate(instant, { zone });
    if (!dt.isValid)
        throw new RangeError(`Invalid instant: ${String(instant)}`);
    return dt;
}
//# sourceMappingURL=time.js.map